/**
 * cart-drawer.js: <cart-drawer> custom element.
 *
 * Contract:
 *   - Opens on click of the header's [data-cart-icon].
 *   - Closes on overlay click, close button click, or Escape.
 *   - Traps focus and locks body scroll while open.
 *   - Qty steppers / remove POST to /cart/change.js, then refresh().
 *   - refresh() re-fetches /cart.js and re-renders lines client-side.
 *   - document event 'cart:refresh' -> refresh().then(open) (Phase 2 add-to-cart hook).
 */
(function () {
  'use strict';

  var CART_URL = '/cart.js';
  var CART_CHANGE_URL = '/cart/change.js';

  function escapeHtml(value) {
    return String(value === null || value === undefined ? '' : value).replace(/[&<>"']/g, function (char) {
      switch (char) {
        case '&':
          return '&amp;';
        case '<':
          return '&lt;';
        case '>':
          return '&gt;';
        case '"':
          return '&quot;';
        case "'":
          return '&#39;';
        default:
          return char;
      }
    });
  }

  // /cart.js returns item.image as a protocol-relative, untransformed CDN
  // URL (unlike Liquid's `image_url: width:` filter used for the initial
  // server render). Normalize the protocol and request a sized variant so
  // client-side re-renders match the server-rendered line images.
  function cartImageSrc(rawUrl, width) {
    if (!rawUrl) return '';
    var url = rawUrl.indexOf('//') === 0 ? 'https:' + rawUrl : rawUrl;
    var sep = url.indexOf('?') === -1 ? '?' : '&';
    return url + sep + 'width=' + width;
  }

  function isVisible(el) {
    return !!(el.offsetWidth || el.offsetHeight || el.getClientRects().length);
  }

  function getFocusableElements(container) {
    if (!container) return [];
    var selector =
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
    return Array.prototype.slice.call(container.querySelectorAll(selector)).filter(isVisible);
  }

  class CartDrawerElement extends HTMLElement {
    constructor() {
      super();
      this.panel = this.querySelector('[data-cart-panel]');
      this.overlay = this.querySelector('[data-cart-overlay]');
      this.closeBtn = this.querySelector('[data-cart-close]');
      this.bodyEl = this.querySelector('[data-cart-body]');
      this.footerEl = this.querySelector('[data-cart-footer]');
      this.linesEl = this.querySelector('[data-cart-lines]');
      this.emptyEl = this.querySelector('[data-cart-empty]');
      this.subtotalEl = this.querySelector('[data-cart-subtotal]');
      this.cartIcon = document.querySelector('[data-cart-icon]');
      this.countEl = document.querySelector('[data-cart-count]');
      this.currency = null;
      this.triggerEl = null;
      this.pendingLines = {};
      this.onKeydown = this.onKeydown.bind(this);
      this.onBodyClick = this.onBodyClick.bind(this);
      this.onDocumentCartRefresh = this.onDocumentCartRefresh.bind(this);
    }

    connectedCallback() {
      if (this.cartIcon) {
        this.cartIcon.addEventListener('click', (event) => {
          event.preventDefault();
          this.triggerEl = this.cartIcon;
          this.open();
        });
      }
      if (this.overlay) this.overlay.addEventListener('click', () => this.close());
      if (this.closeBtn) this.closeBtn.addEventListener('click', () => this.close());
      if (this.bodyEl) this.bodyEl.addEventListener('click', this.onBodyClick);
      document.addEventListener('cart:refresh', this.onDocumentCartRefresh);
    }

    disconnectedCallback() {
      document.removeEventListener('cart:refresh', this.onDocumentCartRefresh);
      document.removeEventListener('keydown', this.onKeydown);
    }

    /* `cart:refresh` means two things: re-read cart state, and (usually) slide
       open, which is what add-to-cart wants. The cart PAGE wants the first
       without the second, so it dispatches
       `new CustomEvent('cart:refresh', { detail: { open: false } })`.
       Omitting detail keeps the original open-on-refresh behaviour. */
    onDocumentCartRefresh(event) {
      var shouldOpen = !(event && event.detail && event.detail.open === false);
      this.refresh()
        .then(() => { if (shouldOpen) this.open(); })
        .catch(function (error) {
          // refresh() already swallows its own errors; this catch exists only
          // as a last line of defence so a rogue rejection can never surface
          // as an unhandled promise rejection.
          console.warn('cart-drawer: cart:refresh handling failed', error);
        });
    }

    get isOpen() {
      return this.hasAttribute('open');
    }

    open() {
      if (this.isOpen) return;
      if (!this.triggerEl && document.activeElement instanceof HTMLElement && document.activeElement !== document.body) {
        this.triggerEl = document.activeElement;
      }
      this.setAttribute('open', '');
      if (this.panel) {
        this.panel.removeAttribute('aria-hidden');
        this.panel.removeAttribute('inert');
      }
      this._prevBodyOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      this._inerted = [];
      var host = this;
      Array.prototype.forEach.call(document.body.children, function (child) {
        if (child.contains(host) || child.tagName === 'SCRIPT' || child.hasAttribute('inert')) return;
        child.setAttribute('inert', '');
        host._inerted.push(child);
      });
      document.addEventListener('keydown', this.onKeydown);
      var focusTarget = this.closeBtn || this.panel;
      window.requestAnimationFrame(function () {
        if (focusTarget && typeof focusTarget.focus === 'function') focusTarget.focus();
      });
    }

    close() {
      if (!this.isOpen) return;
      this.removeAttribute('open');
      if (this.panel) {
        this.panel.setAttribute('aria-hidden', 'true');
        this.panel.setAttribute('inert', '');
      }
      document.body.style.overflow = this._prevBodyOverflow || '';
      (this._inerted || []).forEach(function (el) { el.removeAttribute('inert'); });
      this._inerted = [];
      document.removeEventListener('keydown', this.onKeydown);
      var returnFocusTo = this.triggerEl;
      this.triggerEl = null;
      if (returnFocusTo && typeof returnFocusTo.focus === 'function') {
        returnFocusTo.focus();
      }
    }

    onKeydown(event) {
      if (event.key === 'Escape' || event.key === 'Esc') {
        event.preventDefault();
        this.close();
        return;
      }
      if (event.key === 'Tab') this.trapFocus(event);
    }

    trapFocus(event) {
      if (!this.panel) return;
      var focusable = getFocusableElements(this.panel);
      if (!focusable.length) return;
      var first = focusable[0];
      var last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      } else if (!this.panel.contains(document.activeElement)) {
        event.preventDefault();
        first.focus();
      }
    }

    onBodyClick(event) {
      var minusBtn = event.target.closest('[data-qty-minus]');
      var plusBtn = event.target.closest('[data-qty-plus]');
      var removeBtn = event.target.closest('[data-line-remove]');
      var target = minusBtn || plusBtn || removeBtn;
      if (!target) return;
      event.preventDefault();
      if (target.disabled) return;

      var line = parseInt(target.getAttribute('data-line'), 10);
      if (!line || this.pendingLines[line]) return;

      var lineEl = target.closest('[data-line-item]');
      var qtyEl = lineEl ? lineEl.querySelector('[data-qty-value]') : null;
      var currentQty = qtyEl ? parseInt(qtyEl.textContent, 10) : NaN;
      if (!isFinite(currentQty)) currentQty = 0;

      var quantity;
      if (removeBtn) {
        quantity = 0;
      } else if (plusBtn) {
        quantity = currentQty + 1;
      } else {
        quantity = Math.max(0, currentQty - 1);
      }

      this.changeLine(line, quantity);
    }

    setLineDisabled(line, disabled) {
      if (!this.linesEl) return;
      var lineEl = this.linesEl.querySelector('[data-line-item][data-line="' + line + '"]');
      if (!lineEl) return;
      var buttons = lineEl.querySelectorAll('button');
      for (var i = 0; i < buttons.length; i++) {
        buttons[i].disabled = disabled;
      }
    }

    changeLine(line, quantity) {
      if (this.pendingLines[line]) return;
      this.pendingLines[line] = true;
      this.setLineDisabled(line, true);

      fetch(CART_CHANGE_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ line: line, quantity: quantity }),
      })
        .then((response) => {
          if (!response.ok) throw new Error('cart change request failed with status ' + response.status);
          return this.refresh();
        })
        .catch((error) => {
          // Leave the drawer as-is; nothing was optimistically rendered so
          // there is nothing to roll back.
          console.warn('cart-drawer: failed to update line ' + line, error);
        })
        .then(() => {
          delete this.pendingLines[line];
          this.setLineDisabled(line, false);
        });
    }

    /**
     * Re-fetches /cart.js and re-renders the drawer. Never rejects: on
     * failure it logs a warning and leaves the drawer's current content
     * unchanged, so `.then(open)` chains (e.g. from cart:refresh) are always
     * safe to run and no unhandled rejection can surface.
     */
    refresh() {
      return fetch(CART_URL, { headers: { Accept: 'application/json' } })
        .then((response) => {
          if (!response.ok) throw new Error('cart fetch failed with status ' + response.status);
          return response.json();
        })
        .then((cart) => {
          this.render(cart);
        })
        .catch((error) => {
          console.warn('cart-drawer: unable to refresh cart', error);
        });
    }

    render(cart) {
      if (!cart) return;
      this.currency = cart.currency || this.currency;
      this.updateCount(cart.item_count);

      var items = Array.isArray(cart.items) ? cart.items : [];
      if (items.length === 0) {
        this.renderEmpty();
        return;
      }

      if (this.emptyEl) this.emptyEl.hidden = true;
      if (this.linesEl) {
        this.linesEl.hidden = false;
        this.linesEl.innerHTML = items.map((item, index) => this.renderLine(item, index)).join('');
      }
      if (this.footerEl) this.footerEl.hidden = false;
      if (this.subtotalEl) this.subtotalEl.textContent = this.formatMoney(cart.total_price);
    }

    renderEmpty() {
      if (this.linesEl) {
        this.linesEl.hidden = true;
        this.linesEl.innerHTML = '';
      }
      if (this.footerEl) this.footerEl.hidden = true;
      if (this.emptyEl) this.emptyEl.hidden = false;
    }

    updateCount(count) {
      if (!this.countEl) return;
      var safeCount = typeof count === 'number' && isFinite(count) ? count : 0;
      this.countEl.textContent = String(safeCount);
      this.countEl.hidden = safeCount === 0;
    }

    formatMoney(cents) {
      var amount = (Number(cents) || 0) / 100;
      try {
        return new Intl.NumberFormat('en', {
          style: 'currency',
          currency: this.currency || 'USD',
          currencyDisplay: 'symbol',
        }).format(amount);
      } catch (error) {
        console.warn('cart-drawer: formatMoney failed', error);
        return amount.toFixed(2);
      }
    }

    renderLine(item, index) {
      if (!item) return '';
      var line = index + 1;
      var title = escapeHtml(item.product_title || item.title || '');
      var imageUrl = item.image ? escapeHtml(cartImageSrc(item.image, 120)) : '';
      var quantity = Number(item.quantity) || 0;

      var optionsHtml = '';
      var options = Array.isArray(item.options_with_values) ? item.options_with_values : [];
      var isDefault = options.length === 1 && options[0] && options[0].value === 'Default Title';
      if (options.length && !isDefault) {
        var optionsText = options
          .map(function (opt) {
            return escapeHtml(opt.name) + ': ' + escapeHtml(opt.value);
          })
          .join(', ');
        optionsHtml = '<p class="cart-drawer__line-options">' + optionsText + '</p>';
      }

      var imageHtml = imageUrl
        ? '<img src="' + imageUrl + '" width="60" height="60" alt="' + title + '" loading="lazy">'
        : '';

      return (
        '<li class="cart-drawer__line" data-line-item data-line="' + line + '">' +
        '<div class="cart-drawer__line-image">' + imageHtml + '</div>' +
        '<div class="cart-drawer__line-info">' +
        '<p class="cart-drawer__line-title">' + title + '</p>' +
        optionsHtml +
        '<div class="cart-drawer__line-qty">' +
        '<button type="button" data-qty-minus data-line="' + line + '" aria-label="Decrease quantity">&minus;</button>' +
        '<span data-qty-value>' + quantity + '</span>' +
        '<button type="button" data-qty-plus data-line="' + line + '" aria-label="Increase quantity">+</button>' +
        '</div>' +
        '</div>' +
        '<div class="cart-drawer__line-price">' +
        '<p data-line-price>' + this.formatMoney(item.final_line_price) + '</p>' +
        '<button type="button" class="cart-drawer__line-remove" data-line-remove data-line="' + line + '">Remove</button>' +
        '</div>' +
        '</li>'
      );
    }
  }

  if (!customElements.get('cart-drawer')) {
    customElements.define('cart-drawer', CartDrawerElement);
  }
})();
